import * as React from 'react';
import {
  AddPhotoSheet,
  AddTile,
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  ChecklistQuestion,
  ConfirmDialog,
  EmptyState,
  Field,
  Form,
  FormField,
  Inline,
  Input,
  LightboxStage,
  Loading,
  MobileHeader,
  MobileScreen,
  PassFailToggle,
  PhotoStrip,
  ProgressBar,
  RecordRow,
  SignaturePad,
  Stack,
  Text,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  toast,
  useZodForm,
  StatusChip,
} from '@relay-towing/ui';
import { ArrowLeft, ArrowRight, MapPin, MessageSquarePlus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ChecklistItem,
  InspectionPhotoSource,
  InspectionSubjectType,
  TruckOption,
} from '../../api/truckInspections';
import { useCurrentPosition } from '../../hooks/useCurrentPosition';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import {
  REPORT_PHOTO_ITEM,
  useInspectionRun,
  type InspectionRun,
  type PendingPhoto,
} from '../../hooks/useInspectionRun';
import { useMember } from '../../hooks/useMember';
import { statusLabel, truckName, whenLabel } from '../../lib/inspections';
import { equipmentSchema, signSchema } from '../../lib/inspectionRunSchema';
import { capturePhoto, pickMedia } from '../../lib/nativeBridge';
import { useBack } from '../shell/useBack';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const todayLabel = () =>
  new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

/** What a number question will accept, said plainly under the box. */
const rangeHint = (item: ChecklistItem): string | undefined => {
  if (item.minValue != null && item.maxValue != null)
    return `Between ${item.minValue} and ${item.maxValue}`;
  if (item.minValue != null) return `${item.minValue} or more`;
  if (item.maxValue != null) return `${item.maxValue} or less`;
  return undefined;
};

/**
 * Object URLs for photos still in the outbox, which have no address yet, so
 * a thumbnail can be drawn from the blob. Handed back to the browser when the
 * screen goes.
 */
function usePreviews() {
  const urls = React.useRef(new Map<string, string>());
  React.useEffect(() => {
    const made = urls.current;
    return () => {
      made.forEach((url) => URL.revokeObjectURL(url));
      made.clear();
    };
  }, []);
  return (photo: PendingPhoto) => {
    let url = urls.current.get(photo.localId);
    if (!url) {
      url = URL.createObjectURL(photo.blob);
      urls.current.set(photo.localId, url);
    }
    return url;
  };
}

/** The photo open in the viewer: which strip it is on, and which photo. */
interface Viewing {
  itemId: string;
  id: string;
  src: string;
  pending: boolean;
}

/**
 * Running a checklist on a truck or a piece of equipment, opened from the
 * Inspections tab's Checklists at /inspections/run/:templateId. No Figma
 * frame exists for it: the layout is inferred, and a designer's frame
 * replaces it. It follows the old run screen
 * (screens/inspections/InspectionRunScreen.tsx), whose behaviour it shares
 * through useInspectionRun, and the dashboard's run page, laid out as the
 * other phone screens are: a header, the content, and the send held along
 * the bottom.
 *
 * First what it is being run on: a truck from the list, the member's own
 * first, or a piece of equipment by name; a checklist that covers either
 * asks which. The questions come back from the server once that is named.
 * Each is a ChecklistQuestion, its note and photos in a well that turns red
 * on a failed answer. Photos go into the outbox and up from there, so a
 * yard with patchy signal loses none; one still waiting shows a spinner and
 * holds the send. A checklist that asks for a signature is signed on its
 * own page. Sent, the report opens.
 *
 * Needs "Create inspections" (`inspections.create`), which the server
 * enforces; without it the screen says so rather than offering a run it
 * would refuse.
 */
export function InspectionRunScreen() {
  const { templateId = '' } = useParams<{ templateId: string }>();
  const back = useBack('/inspections');
  const member = useMember();

  if (member.isLoading)
    return <Loading label="Opening the checklist" fill="screen" />;
  if (!member.can('inspections.create'))
    return (
      <MobileScreen
        header={
          <MobileHeader
            onBack={back}
            backLabel="Back to inspections"
            title="Inspection"
          />
        }
      >
        <EmptyState
          title="Your role cannot run checklists"
          description="Running one needs the Create inspections permission. An owner or admin can give it on the Roles page."
          action={<Button onClick={back}>Back to inspections</Button>}
        />
      </MobileScreen>
    );
  return <RunChecklist key={templateId} templateId={templateId} />;
}

function RunChecklist({ templateId }: { templateId: string }) {
  const navigate = useNavigate();
  const { data: profile } = useDriverProfile();
  const here = useCurrentPosition();
  const run = useInspectionRun({
    templateId,
    signerName: profile?.name?.trim() ?? '',
    notify: toast.error,
  });
  const toList = () => navigate('/inspections');

  // A checklist that is not on this member's list cannot be run; the hook
  // says when, and the list is where they go.
  React.useEffect(() => {
    if (!run.unavailable) return;
    toast.error(
      run.unavailable === 'error'
        ? 'Could not load the checklist'
        : 'That checklist is not available'
    );
    navigate('/inspections', { replace: true });
  }, [run.unavailable, navigate]);

  // Sent, the report opens in place of the run, so back goes to the list
  // rather than to a checklist already filed.
  React.useEffect(() => {
    if (!run.filed) return;
    toast.success('Inspection submitted');
    navigate(
      run.filed.id ? `/inspections/report/${run.filed.id}` : '/inspections',
      { replace: true }
    );
  }, [run.filed, navigate]);

  if (run.starting || run.checklists.isPending || !run.row || run.filed)
    return <Loading label="Opening the checklist" fill="screen" />;

  if (run.stage === 'subject')
    return <SubjectStage run={run} onBack={toList} />;
  if (!run.template)
    return <Loading label="Opening the checklist" fill="screen" />;
  if (run.stage === 'sign')
    return <SignStage run={run} location={here.position} />;
  return <ChecklistStage run={run} here={here} onBack={toList} />;
}

/**
 * What is being checked, named before a single question is drawn. A
 * checklist written for trucks lists them. One written for equipment asks
 * for a name, because equipment has no id to pick from. One that covers
 * either asks which of the two first.
 */
function SubjectStage({
  run,
  onBack,
}: {
  run: InspectionRun;
  onBack: () => void;
}) {
  const header = (
    <MobileHeader
      onBack={onBack}
      backLabel="Back to inspections"
      title={run.row?.name}
    />
  );

  return (
    <MobileScreen header={header}>
      <Stack gap={4}>
        <Text as="h2" variant="h3">
          {run.covers === 'TRUCK' ? 'Which truck?' : 'What are you checking?'}
        </Text>
        {/* Only a checklist that covers either asks. The other two already
            know what they are written for. */}
        {run.covers === 'ANY' ? (
          <ToggleGroup
            type="single"
            value={run.kind}
            onValueChange={(value) => {
              if (value) run.setSubjectKind(value as InspectionSubjectType);
            }}
            variant="outline"
            size="lg"
            gap={2}
            bordered={false}
            aria-label="What are you checking"
          >
            <ToggleGroupItem value="TRUCK">Truck</ToggleGroupItem>
            <ToggleGroupItem value="EQUIPMENT">Other</ToggleGroupItem>
          </ToggleGroup>
        ) : null}
        {run.kind === 'TRUCK' ? (
          <TruckList run={run} />
        ) : (
          <EquipmentForm run={run} />
        )}
      </Stack>
    </MobileScreen>
  );
}

function TruckList({ run }: { run: InspectionRun }) {
  const { trucks } = run;
  if (trucks.isPending) return <Loading label="Loading trucks" />;
  // A list that did not arrive is not an empty yard. Saying "no trucks"
  // here is how a broken company header stays invisible.
  if (trucks.isError)
    return (
      <EmptyState
        title="Could not load your trucks"
        description="Check your signal and try again."
        action={
          <Button variant="outline" onClick={() => void trucks.refetch()}>
            Try again
          </Button>
        }
      />
    );
  const all = trucks.data ?? [];
  if (all.length === 0)
    return (
      <EmptyState
        title="No trucks on file"
        description="Ask the office to add one."
      />
    );
  const assigned = all.filter((one) => one.isAssignedToMe);
  const rest = all.filter((one) => !one.isAssignedToMe);

  const row = (one: TruckOption) => {
    const name = truckName(one);
    // The plate only earns its place when the name is the door number.
    const plate = one.truckNumber ? one.licensePlate : null;
    const checked = one.lastCheck
      ? `Checked ${whenLabel(one.lastCheck.submittedAt)}`
      : 'Not checked yet';
    return (
      <RecordRow
        key={one.id}
        title={name}
        meta={[plate, checked].filter(Boolean).join(' · ')}
        aria-label={`Check ${name}`}
        onClick={() => run.startOn({ truckId: one.id, label: name })}
      />
    );
  };

  const group = (title: string, list: TruckOption[]) =>
    list.length ? (
      <Stack gap={2}>
        {/* Headed only when there are both kinds to tell apart. */}
        {assigned.length && rest.length ? (
          <Text as="h3" variant="overline" tone="muted">
            {title}
          </Text>
        ) : null}
        <Stack gap={0} divided>
          {list.map(row)}
        </Stack>
      </Stack>
    ) : null;

  return (
    <Stack gap={4}>
      {group('Assigned to you', assigned)}
      {group('Other trucks', rest)}
    </Stack>
  );
}

function EquipmentForm({ run }: { run: InspectionRun }) {
  const form = useZodForm(equipmentSchema, {
    defaultValues: { equipmentLabel: run.equipmentLabel },
  });
  // What the checklist was last run on arrives after the form is drawn, and
  // fills the box unless something has been typed in it.
  React.useEffect(() => {
    if (!form.getFieldState('equipmentLabel').isDirty)
      form.setValue('equipmentLabel', run.equipmentLabel);
  }, [run.equipmentLabel, form]);

  return (
    <Form
      form={form}
      gap={4}
      onSubmit={({ equipmentLabel }) =>
        run.startOn({ equipmentLabel, label: equipmentLabel })
      }
    >
      <FormField form={form} name="equipmentLabel" label="Name it">
        {(field) => <Input {...field} placeholder="e.g. Dolly 2" />}
      </FormField>
      <Button type="submit" size="xl" fullWidth trailingIcon={<ArrowRight />}>
        Start
      </Button>
    </Form>
  );
}

function ChecklistStage({
  run,
  here,
  onBack,
}: {
  run: InspectionRun;
  here: ReturnType<typeof useCurrentPosition>;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const preview = usePreviews();
  const template = run.template!;
  const data = run.data;
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState<string | null>(null);
  const [viewing, setViewing] = React.useState<Viewing | null>(null);
  const [changing, setChanging] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  // What the server calls this run's subject: a truck in the member's words,
  // or the name typed for a piece of equipment.
  const name = data?.subject.label ?? '';
  // The word for it in a sentence, so the photo prompts do not tell someone
  // holding a dolly to photograph the truck.
  const subjectWord =
    data?.subject.type === 'EQUIPMENT' ? 'equipment' : 'truck';
  // Only when the name is the door number does the plate still have
  // something to add; otherwise the name already is the plate.
  const plate = data?.truck?.truckNumber ? data.truck.licensePlate : null;
  const makeModel = [data?.truck?.make, data?.truck?.model]
    .filter(Boolean)
    .join(' ');
  const last = data?.lastInspection;

  const sections: [string, ChecklistItem[]][] = [];
  run.items.forEach((item) => {
    const key = item.section || 'Checklist';
    const found = sections.find(([section]) => section === key);
    if (found) found[1].push(item);
    else sections.push([key, [item]]);
  });

  /** The strip's photos: those up, then those still in the outbox. */
  const photosOn = (
    itemId: string,
    urls: string[]
  ): { id: string; src: string; status?: 'sending' }[] => [
    ...urls.map((url) => ({ id: url, src: url })),
    ...run.pendingFor(itemId).map((photo) => ({
      id: photo.localId,
      src: preview(photo),
      status: 'sending' as const,
    })),
  ];

  const open = (itemId: string, urls: string[]) => (id: string) => {
    const photo = photosOn(itemId, urls).find((one) => one.id === id);
    if (photo)
      setViewing({
        itemId,
        id,
        src: photo.src,
        pending: Boolean(photo.status),
      });
  };

  const removeViewed = () => {
    if (!viewing) return;
    if (viewing.pending) run.removePending(viewing.itemId, viewing.id);
    else run.removePhoto(viewing.itemId, viewing.id);
    setViewing(null);
  };

  // Each way in says which picker was opened; the report records it.
  const add = (source: InspectionPhotoSource) => () => {
    const itemId = adding;
    if (!itemId) return;
    const picked =
      source === 'CAMERA' ? capturePhoto() : pickMedia({ allowVideo: false });
    void picked.then(({ files }) => run.addFiles(itemId, files, source));
  };

  /** Says what still stands in the way, and opens the question it is on. */
  const blocked = () => {
    const block = run.firstBlock();
    if (!block) return false;
    toast.error(block.message);
    if (block.itemId) setExpanded(block.itemId);
    return true;
  };

  const next = () => {
    if (blocked()) return;
    if (template.requireSignature) run.setStage('sign');
    else setConfirming(true);
  };

  const changeSubject = () => {
    run.changeSubject();
    setChanging(false);
    setExpanded(null);
  };

  const answer = (item: ChecklistItem) => {
    const draft = run.draftFor(item.id);
    switch (item.answerType) {
      case 'PASS_FAIL':
        return (
          <PassFailToggle
            label={item.label}
            value={draft.status}
            onValueChange={(status) => {
              run.setDraft(item.id, { status });
              // Fail opens the well. Passing shuts it again, unless a note
              // or a photo is already in there.
              setExpanded((current) =>
                status === 'FAIL'
                  ? item.id
                  : current === item.id
                    ? null
                    : current
              );
            }}
          />
        );
      case 'YES_NO':
      case 'SELECT':
        return (
          <ToggleGroup
            type="single"
            value={draft.valueText ?? ''}
            onValueChange={(value) => {
              if (value) run.setDraft(item.id, { valueText: value });
            }}
            variant="outline"
            size="lg"
            gap={2}
            wrap
            bordered={false}
            aria-label={item.label}
          >
            {(item.answerType === 'YES_NO' ? ['Yes', 'No'] : item.options).map(
              (option) => (
                <ToggleGroupItem key={option} value={option}>
                  {option}
                </ToggleGroupItem>
              )
            )}
          </ToggleGroup>
        );
      case 'CHECKBOX':
        return (
          <ToggleGroup
            type="multiple"
            value={draft.valueList}
            onValueChange={(valueList) => run.setDraft(item.id, { valueList })}
            variant="outline"
            size="lg"
            gap={2}
            wrap
            bordered={false}
            aria-label={item.label}
          >
            {item.options.map((option) => (
              <ToggleGroupItem key={option} value={option}>
                {option}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        );
      case 'NUMBER': {
        const hint = rangeHint(item);
        const bad = run.outOfRange.some((one) => one.id === item.id);
        return (
          <Field
            description={[item.unit, hint].filter(Boolean).join(' · ')}
            error={bad ? (hint ?? 'Enter a number') : undefined}
          >
            <Input
              inputMode="decimal"
              value={draft.valueNumber ?? ''}
              onChange={(event) =>
                run.setDraft(item.id, { valueNumber: event.target.value })
              }
              placeholder="0"
              aria-label={item.label}
            />
          </Field>
        );
      }
      case 'TEXT':
        return (
          <Textarea
            value={draft.valueText ?? ''}
            onChange={(event) =>
              run.setDraft(item.id, { valueText: event.target.value })
            }
            rows={2}
            placeholder="Type here"
            aria-label={item.label}
          />
        );
      default:
        // A photo question: the photos below are its answer.
        return null;
    }
  };

  const question = (item: ChecklistItem) => {
    const draft = run.draftFor(item.id);
    const failed = draft.status === 'FAIL';
    // A photo question shows its photos from the start, the same as a
    // question that always asks for one.
    const showPhotos =
      item.answerType === 'PHOTO' || item.photoRule === 'ALWAYS';
    const photos = photosOn(item.id, draft.photoUrls);
    const needsPhoto =
      photos.length === 0 &&
      (item.photoRule === 'ALWAYS' ||
        (item.answerType === 'PHOTO' && item.isRequired) ||
        (item.photoRule === 'ON_FAIL' && failed));
    const wellOpen =
      failed ||
      showPhotos ||
      photos.length > 0 ||
      Boolean(draft.note) ||
      expanded === item.id;

    // One quiet line rather than a row of badges: what the question will
    // and will not let the member skip.
    const rules: string[] = [];
    if (!item.isRequired) rules.push('Optional');
    if (item.photoRule === 'ALWAYS') rules.push('Photo needed');
    else if (item.photoRule === 'ON_FAIL')
      rules.push('Photo needed if it fails');

    return (
      <ChecklistQuestion
        key={item.id}
        label={item.label}
        help={item.helpText}
        rules={rules.join(' · ') || undefined}
        failed={failed}
        detailTitle={
          failed
            ? 'Document the issue'
            : showPhotos
              ? 'Add a photo'
              : 'Add a note or photo'
        }
        detail={
          wellOpen ? (
            <>
              <Textarea
                value={draft.note ?? ''}
                onChange={(event) =>
                  run.setDraft(item.id, { note: event.target.value })
                }
                rows={2}
                placeholder={
                  failed
                    ? "Describe what's wrong"
                    : 'Anything worth mentioning?'
                }
                aria-label={`Note about ${item.label}`}
              />
              <PhotoStrip
                size="sm"
                photos={photos}
                onOpen={open(item.id, draft.photoUrls)}
                onAdd={() => setAdding(item.id)}
                addLabel={failed ? 'Add photo of issue' : 'Add photo'}
                aria-label={`Photos for ${item.label}`}
              />
            </>
          ) : undefined
        }
        message={
          wellOpen && needsPhoto
            ? failed
              ? 'Photograph what is wrong before you send this'
              : 'Add a photo before you send this'
            : undefined
        }
      >
        {answer(item)}
        {/* A pass is not always the whole story. A headlight that works
            but is going dim is the thing a fleet manager wants weeks
            before it fails. */}
        {wellOpen ? null : (
          <Inline>
            <Button
              variant="link"
              size="inline"
              leadingIcon={<MessageSquarePlus />}
              onClick={() => setExpanded(item.id)}
            >
              Add a note or photo
            </Button>
          </Inline>
        )}
      </ChecklistQuestion>
    );
  };

  const reportPhotos = photosOn(REPORT_PHOTO_ITEM, run.reportPhotoUrls);

  const footer = (
    <Stack gap={2}>
      {run.statusLine ? (
        <Text variant="caption" tone="muted" align="center">
          {run.statusLine}
        </Text>
      ) : null}
      <Button
        size="xl"
        fullWidth
        trailingIcon={template.requireSignature ? undefined : <ArrowRight />}
        onClick={next}
      >
        {template.requireSignature ? 'Continue to sign' : 'Submit inspection'}
      </Button>
    </Stack>
  );

  return (
    <MobileScreen
      header={
        <MobileHeader
          onBack={onBack}
          backLabel="Back to inspections"
          title="Inspection"
        />
      }
      footer={footer}
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Stack gap={0}>
            <Text as="h2" variant="h2">
              {name}
            </Text>
            {makeModel && makeModel !== name ? (
              <Text variant="body-sm" tone="muted">
                {makeModel}
              </Text>
            ) : null}
          </Stack>
          <Inline gap={2} wrap align="center">
            <Text variant="caption" tone="muted">
              {template.name}
            </Text>
            {plate ? (
              <Text variant="caption" tone="muted" numeric>
                {plate}
              </Text>
            ) : null}
            <Text variant="caption" tone="muted">
              {todayLabel()}
            </Text>
            {run.online ? null : <StatusChip label="Offline" tone="warning" />}
          </Inline>
          <Inline>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<ArrowLeft />}
              onClick={() =>
                run.hasWork() ? setChanging(true) : changeSubject()
              }
            >
              Change what you&apos;re checking
            </Button>
          </Inline>
        </Stack>

        {/* The last check on this same truck or piece of equipment. The
            run is kept on the phone while its report is open. */}
        {last ? (
          <RecordRow
            title={`Last check · ${statusLabel(last.status)}`}
            meta={`Checked ${whenLabel(last.submittedAt)}${
              last.driver?.name || last.signedName
                ? ` by ${last.driver?.name ?? last.signedName}`
                : ''
            }`}
            onClick={() => navigate(`/inspections/report/${last.id}`)}
          />
        ) : null}

        <Stack gap={2}>
          <Inline gap={3} align="center" justify="between">
            <Text variant="caption-strong">
              {run.doneCount} of {run.required.length} checked
            </Text>
            <Text variant="caption" tone="muted" numeric>
              {run.progress}%
            </Text>
          </Inline>
          <ProgressBar
            value={run.progress}
            label={`${run.doneCount} of ${run.required.length} questions answered`}
          />
        </Stack>

        {sections.map(([section, items]) => (
          <Stack key={section} gap={3}>
            <Text as="h3" variant="overline" tone="muted">
              {section}
            </Text>
            {items.map(question)}
          </Stack>
        ))}

        <Field label="Note">
          <Textarea
            value={run.notes}
            onChange={(event) => run.setNotes(event.target.value)}
            rows={3}
            placeholder="Anything else the shop should know?"
          />
        </Field>

        {/* A photo of the thing being checked rather than of any one
            question. */}
        <Field label={`Photos of the ${subjectWord}`}>
          {reportPhotos.length ? (
            <PhotoStrip
              size="sm"
              photos={reportPhotos}
              onOpen={open(REPORT_PHOTO_ITEM, run.reportPhotoUrls)}
              onAdd={() => setAdding(REPORT_PHOTO_ITEM)}
              aria-label={`Photos of the ${subjectWord}`}
            />
          ) : (
            <AddTile width="block" onClick={() => setAdding(REPORT_PHOTO_ITEM)}>
              Add a photo of the {subjectWord}
            </AddTile>
          )}
        </Field>

        <Field
          label="Where you are"
          description="Goes on the report, so the office knows where this was done."
        >
          {here.position ? (
            <Inline gap={3} align="center" justify="between" wrap>
              <Stack gap={0}>
                <Text variant="caption-strong">Pinned</Text>
                <Text variant="caption" tone="muted" numeric>
                  {here.position.latitude.toFixed(5)},{' '}
                  {here.position.longitude.toFixed(5)}
                </Text>
              </Stack>
              <Button
                variant="ghost"
                size="sm"
                loading={here.locating}
                onClick={here.locate}
              >
                Pin again
              </Button>
            </Inline>
          ) : (
            <Stack gap={2}>
              <Button
                variant="outline"
                fullWidth
                leadingIcon={<MapPin />}
                loading={here.locating}
                onClick={here.locate}
              >
                Pin where you are
              </Button>
              {here.failed ? (
                <Text variant="caption" tone="muted">
                  Your phone did not share a location. Turn location on for this
                  app, or send the report without it.
                </Text>
              ) : null}
            </Stack>
          )}
        </Field>
      </Stack>

      <AddPhotoSheet
        open={adding !== null}
        onOpenChange={(next) => !next && setAdding(null)}
        title="Add a photo"
        onTakePhoto={add('CAMERA')}
        onChooseFromLibrary={add('UPLOAD')}
      />
      <BottomSheet
        open={viewing !== null}
        onOpenChange={(next) => !next && setViewing(null)}
      >
        <BottomSheetContent
          title="Photo"
          size="full"
          footer={
            <Button
              variant="destructive-secondary"
              size="xl"
              pill
              onClick={removeViewed}
            >
              Remove photo
            </Button>
          }
        >
          {viewing ? (
            <LightboxStage src={viewing.src} alt="Inspection photo" />
          ) : null}
        </BottomSheetContent>
      </BottomSheet>
      <ConfirmDialog
        open={changing}
        onOpenChange={setChanging}
        title="Start over on something else?"
        description="The answers on this one are cleared. Photos you have taken go with them."
        cancelLabel="Keep answering"
        confirmLabel="Start over"
        onConfirm={changeSubject}
      />
      <SubmitConfirm
        run={run}
        open={confirming}
        onOpenChange={setConfirming}
        location={here.position}
      />
    </MobileScreen>
  );
}

/**
 * The last word before anything is sent. Both ways out of the run end
 * here: the checklist's own submit for a checklist with no signature, and
 * the send on the signing page.
 */
function SubmitConfirm({
  run,
  open,
  onOpenChange,
  location,
}: {
  run: InspectionRun;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: ReturnType<typeof useCurrentPosition>['position'];
}) {
  const flagged = run.failedItems.length;
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Submit inspection?"
      description={
        flagged > 0
          ? `You flagged ${plural(flagged, 'issue')}. This goes to the shop and can't be edited after submitting.`
          : "It can't be edited after submitting."
      }
      confirmLabel="Submit"
      loading={run.sending}
      onConfirm={() => {
        void run.finish(location).then((sent) => sent && onOpenChange(false));
      }}
    />
  );
}

/**
 * Signing, for a checklist that asks for it: the faults being reported, the
 * member's name and their signature, then the send.
 */
function SignStage({
  run,
  location,
}: {
  run: InspectionRun;
  location: ReturnType<typeof useCurrentPosition>['position'];
}) {
  const [confirming, setConfirming] = React.useState(false);
  const form = useZodForm(signSchema, {
    defaultValues: { signedName: run.signedName, signature: '' },
  });
  const formId = React.useId();
  const failed = run.failedItems;
  const template = run.template!;

  return (
    <MobileScreen
      header={
        <MobileHeader
          onBack={() => run.setStage('checklist')}
          backLabel="Back to the checklist"
          title="Sign the report"
        />
      }
      footer={
        <Stack gap={2}>
          {run.statusLine ? (
            <Text variant="caption" tone="muted" align="center">
              {run.statusLine}
            </Text>
          ) : null}
          <Button
            type="submit"
            form={formId}
            size="xl"
            fullWidth
            loading={run.sending}
          >
            Send report
          </Button>
        </Stack>
      }
    >
      <Stack gap={6}>
        <Stack gap={1}>
          <Text as="h2" variant="h2">
            {failed.length === 0
              ? 'Nothing to report'
              : `You are reporting ${plural(failed.length, 'fault')}`}
          </Text>
          <Text variant="caption" tone="muted">
            {run.data?.subject.label} · {template.name}
          </Text>
        </Stack>
        {failed.length > 0 ? (
          <Alert tone="destructive" title="Faults">
            <Stack gap={1}>
              {failed.map((item) => (
                <Text key={item.id} variant="caption-strong" tone="inherit">
                  {item.label}
                </Text>
              ))}
            </Stack>
          </Alert>
        ) : null}
        <Form
          form={form}
          id={formId}
          gap={4}
          onSubmit={(values) => {
            run.setSignedName(values.signedName);
            run.setSignature(values.signature);
            setConfirming(true);
          }}
        >
          <FormField form={form} name="signedName" label="Your name">
            {(field) => (
              <Input
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  // Kept on the phone with the rest of the run.
                  run.setSignedName(event.target.value);
                }}
              />
            )}
          </FormField>
          <FormField
            form={form}
            name="signature"
            label="Signature"
            description="Signing says the report is true."
          >
            {(field) => (
              <SignaturePad
                value={field.value}
                onChange={(dataUrl) => {
                  field.onChange(dataUrl);
                  run.setSignature(dataUrl);
                }}
              />
            )}
          </FormField>
        </Form>
      </Stack>
      <SubmitConfirm
        run={run}
        open={confirming}
        onOpenChange={setConfirming}
        location={location}
      />
    </MobileScreen>
  );
}
